import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { getAllShutdownEventsForAsset } from "@/api/downtime-api.js";
import { RCA_LIST_RESULTS_LIMIT } from "@/constants/rca-constants.js";
import AssetLayout from "./asset-layout.jsx";
import AlarmHistoryList from "@/components/root-cause-analysis/alarm-history-list.jsx";
import DowntimeHistoryList from "@/components/root-cause-analysis/downtime-history-list.jsx";
import ShutdownEventsList from "@/components/root-cause-analysis/shutdown-events-list.jsx";
import Measurements from "@/components/root-cause-analysis/measurements.jsx";
import RcaAiAnalysis from "@/components/root-cause-analysis/rca-ai-analysis.jsx";
import { useUserAccess } from "@/hooks/useUserAccess.js";

/**
 * Root Cause Analysis page: route-bound view for an asset (placeholder layout).
 * Shutdown column keeps a max viewport-based height with an internal list scroll; alarm/downtime use bounded panels.
 * Measurements and key-sensor charts extend the page so the asset layout main area scrolls vertically.
 *
 * @returns {JSX.Element}
 */
export default function RootCauseAnalysis() {
  const {
    canRunRcaAi,
    showRcaAi,
    isLoading: isUserAccessLoading,
  } = useUserAccess();
  const { id } = useParams();
  const assetId = decodeURIComponent(id);
  const { assets: compressors } = useAssets();
  const compressor = compressors?.find(
    (c) =>
      c.assetNumber === assetId
      || c.assetNumber === id
      || c.id === assetId
      || c.id === id
  );

  /** Context for Alarm / Downtime panels when a shutdown row is selected (ISO reporting date). */
  const [selectedShutdown, setSelectedShutdown] = useState(
    /** @type {{ assetId: string|number, reportingDate: string, uniqueId: number } | null} */ (
      null
    )
  );

  const [shutdownEvents, setShutdownEvents] = useState([]);
  const [shutdownEventsLoading, setShutdownEventsLoading] = useState(true);
  const [shutdownEventsError, setShutdownEventsError] = useState(
    /** @type {string | null} */ (null)
  );
  const [measurementContext, setMeasurementContext] = useState(
    /** @type {{ rows: unknown[], hoursBack: number, isLoading: boolean, hasShutdownAnchor: boolean }} */ ({
      rows: [],
      hoursBack: 6,
      isLoading: false,
      hasShutdownAnchor: false,
    })
  );

  useEffect(() => {
    setSelectedShutdown(null);
  }, [compressor?.id]);

  useEffect(() => {
    if (compressor?.id == null || compressor.id === "") {
      setShutdownEvents([]);
      setShutdownEventsLoading(false);
      setShutdownEventsError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setShutdownEventsLoading(true);
      setShutdownEventsError(null);
      try {
        const { events: rows } = await getAllShutdownEventsForAsset(
          compressor.id
        );
        if (!cancelled) {
          setShutdownEvents(Array.isArray(rows) ? rows : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("getAllShutdownEventsForAsset failed:", err);
          setShutdownEvents([]);
          setShutdownEventsError("Failed to load shutdown events");
        }
      } finally {
        if (!cancelled) setShutdownEventsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [compressor?.id]);

  return (
    <AssetLayout>
      {compressor ? (
        <div className='w-full min-h-0 min-w-0 max-w-full overflow-x-hidden'>
          <div className='flex w-full flex-col gap-6 lg:flex-row lg:items-start'>
            <div className='w-full shrink-0 lg:w-auto'>
              <ShutdownEventsList
                fleetAssetId={compressor.id}
                shutdownEvents={shutdownEvents}
                shutdownEventsLoading={shutdownEventsLoading}
                shutdownEventsError={shutdownEventsError}
                resultsLimit={RCA_LIST_RESULTS_LIMIT}
                selectedUniqueId={selectedShutdown?.uniqueId ?? null}
                onSelectShutdownEvent={setSelectedShutdown}
              />
            </div>
            {/* Main RCA column grows with content; page scroll is handled by AssetLayout main `overflow-auto`. */}
            <div className='flex min-h-0 w-full min-w-0 flex-1 flex-col gap-6'>
              <div className='flex w-full flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-6'>
                <div className='flex min-h-[12rem] max-h-[min(42vh,22rem)] min-w-0 w-full flex-col lg:flex-1'>
                  <AlarmHistoryList
                    assetId={compressor.id}
                    reportingDate={selectedShutdown?.reportingDate ?? null}
                    resultsLimit={RCA_LIST_RESULTS_LIMIT}
                  />
                </div>
                <div className='flex min-h-[12rem] max-h-[min(42vh,22rem)] min-w-0 w-full flex-col lg:flex-1'>
                  <DowntimeHistoryList
                    shutdownEvents={shutdownEvents}
                    shutdownEventsLoading={shutdownEventsLoading}
                    shutdownEventsError={shutdownEventsError}
                    reportingDate={selectedShutdown?.reportingDate ?? null}
                    selectedUniqueId={selectedShutdown?.uniqueId ?? null}
                  />
                </div>
              </div>
              <div className='flex w-full min-w-0 flex-col gap-3'>
                {showRcaAi ? (
                  <RcaAiAnalysis
                    canUseAiAnalysis={canRunRcaAi}
                    isAccessLoading={isUserAccessLoading}
                    downtimeEventId={selectedShutdown?.uniqueId ?? null}
                    startDate={selectedShutdown?.reportingDate ?? null}
                    assetId={compressor.id}
                    assetNumber={compressor.assetNumber}
                    measurementRows={measurementContext.rows}
                    trendWindowHours={measurementContext.hoursBack}
                    measurementsLoading={measurementContext.isLoading}
                    hasShutdownSelected={measurementContext.hasShutdownAnchor}
                  />
                ) : null}
                <Measurements
                  assetId={compressor.id}
                  reportingDate={selectedShutdown?.reportingDate ?? null}
                  onContextChange={setMeasurementContext}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </AssetLayout>
  );
}
